import React, {useRef, useEffect, useMemo, useState, useCallback} from 'react';
import {Box, Flex, Text, ActionIcon, Transition, Popover, Button} from '@mantine/core';
import {useSearchParams} from 'react-router-dom';
import {showNotification} from '@mantine/notifications';
import type {DiscoverySessionMessage} from '../../api/discoveryAgent';
import {fetchArtifactById, sendDiscoveryMessage, sendDiscoveryMessageWithRecovery, updateMessageHidden, fetchOnePager, shareDiscoverySession} from '../../api/discoveryAgent';
import {
  IconCategory,
  IconChevronLeft,
  IconChevronRight,
  IconGrowth,
  IconHeartHandshake,
  IconLayoutSidebarLeftExpand,
  IconShare,
  IconSwords,
  IconTrendingDown
} from '@tabler/icons';
import {useAuth0} from '@auth0/auth0-react';
import {useDiscoveryStore, type DiscoveryStore} from '../../stores/useDiscoveryStore';
import {useDiscoverySessionUrl} from '../../hooks/useDiscoverySessionUrl';
import {useAbortOnUnmount} from '../../hooks/useAbortOnUnmount';
import { useMutation } from '@tanstack/react-query';
import {useDiscoveryMessages, useSessionDiscoveryIdeas, useActiveDiscoverySession} from '../../hooks/useDiscoveryAgent';
import {ExploreBagelSparkleIcon} from './ExploreBagelSparkleIcon';
import ExploreButton from './components/ExploreButton';
import ExploreChat from './components/ExploreChat';
import ExploreChatMessageList from './components/ExploreChatMessageList';
import {usePrompts} from "../../hooks/useAiAgent";
import _ from "lodash";
import { CreateIdeaButton } from '../../pages/ai-agent/createIdea/CreateIdeaButton';
import { CreateIdeaFromChatModal } from '../../pages/ai-agent/createIdea/CreateIdeaFromChatModal';
import {
  buildCreateIdeaOptions,
  buildCreateIdeaHiddenMessage,
  isHiddenCreateIdeaUserMessage,
  type CreateIdeaOption,
} from '../../pages/ai-agent/createIdea/createIdeaFromChatPrompt';
import { useExploreIdeaIdUrlParam } from './hooks/useExploreIdeaIdUrlParam';
import { parseIdeasPageParam } from './hooks/useExploreIdeasListArtifactUi';

function getGreetingName(user: { name?: string | null; email?: string | null } | undefined): string {
  if (user?.name?.trim()) return user.name.trim().split(/\s+/)[0] ?? 'there';
  if (user?.email) return user.email.split('@')[0] ?? 'there';
  return 'there';
}

const GET_STARTED_CARDS_PER_VIEW = 2;

/**
 * The create-idea generation is a short hidden prompt, not a full analysis,
 * and the modal awaits it. Recover from a dropped request for a short while,
 * then fail rather than block the user.
 */
const CREATE_IDEA_RECOVERY_BUDGET_MS = 90 * 1000;

interface ExploreMainContentProps {
  isLeftSidebarCollapsed?: boolean;
  onExpandLeftSidebar?: () => void;
  handleLeftPanelCollapse?: () => void;
}

export const ExploreMainContent = ({ isLeftSidebarCollapsed, onExpandLeftSidebar, handleLeftPanelCollapse }: ExploreMainContentProps) => {
  const auth0 = useAuth0();
  const { user, getAccessTokenSilently } = auth0;
  const showExploreTopSegmentControl =
    (window.__RUNTIME_CONFIG__ as any).REACT_APP_SHOW_EXPLORE_TOP_SEGMENT_CONTROL === 'true';
  const setInputValue = useDiscoveryStore((s: DiscoveryStore) => s.setInputValue);
  const setDiscoveryPromptCategory = useDiscoveryStore((s: DiscoveryStore) => s.setDiscoveryPromptCategory);
  const {sessionId, setSessionId} = useDiscoverySessionUrl();
  const {prompts} = usePrompts('discovery');
  const effectiveSessionId = sessionId ?? '';

  const { session } = useActiveDiscoverySession();
  const isBackendProcessing =
    session?.chatStatus === 'processing' &&
    !!session?.chatStatusUpdatedAt &&
    Date.now() - new Date(session.chatStatusUpdatedAt).getTime() < 15 * 60 * 1000;

  const {
    messages,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = useDiscoveryMessages(effectiveSessionId, {
    enabled: !!effectiveSessionId,
    isBackendProcessing,
  });
  const { artifactIdeaIdByAlias, refetch: refetchSessionIdeas } = useSessionDiscoveryIdeas(effectiveSessionId);
  const runUntilUnmount = useAbortOnUnmount();
  const isNewSession = useDiscoveryStore((s: DiscoveryStore) => s.isNewSession);
  const optimisticUserMessage = useDiscoveryStore((s: DiscoveryStore) => s.optimisticUserMessage);
  const isMessageSending = useDiscoveryStore((s: DiscoveryStore) => s.isMessageSending);
  const messageSendingSessionId = useDiscoveryStore((s: DiscoveryStore) => s.messageSendingSessionId);
  /** React Query mutation for the hidden create-idea API call.
   * isPending replaces the old manual isCreateIdeaSending boolean state,
   * blocking the composer while the hidden prompt/reply pair is in flight. */
  const createIdeaContentMutation = useMutation<{ title: string; description: string; alreadyPersisted?: boolean }, Error, void>({
    mutationFn: async () => {
      // Pre-filled from a suggested_idea artifact — return immediately, no API call.
      // Read from the ref to avoid stale-closure issues when state + modal open
      // are batched into the same React flush.
      const prefill = suggestedIdeaContentRef.current;
      if (prefill) {
        suggestedIdeaContentRef.current = null;
        setSuggestedIdeaContent(null);
        return { ...prefill };
      }
      if (!createIdeaSelectedOption || !effectiveSessionId) {
        return { title: '', description: '' };
      }

      // ── Step 1: if a saved one-pager already exists, return it immediately.
      if (createIdeaSelectedOption.messageId) {
        const existing = await fetchOnePager(
          effectiveSessionId,
          createIdeaSelectedOption.messageId,
          { getAccessTokenSilently }
        );
        if (existing) {
          // Flag so the modal skips its createOnePager POST — this content was
          // just read from the API and re-POSTing it would be a redundant write.
          return { title: existing.title, description: existing.description, alreadyPersisted: true };
        }
      }

      // ── Step 2: no saved one-pager — generate via chat API (hidden message).
      //    The modal will POST-save the result in step 3 via its own createOnePager call.
      const hiddenMsg = buildCreateIdeaHiddenMessage(createIdeaSelectedOption.payload);
      // The modal blocks on this, so cap the recovery poll well short of the
      // default ten minutes — a short generation that dropped is better
      // reported as failed than left spinning.
      const result = await runUntilUnmount((signal) =>
        sendDiscoveryMessageWithRecovery(
          effectiveSessionId,
          hiddenMsg,
          { getAccessTokenSilently },
          undefined,
          { hidden: true, recoveryBudgetMs: CREATE_IDEA_RECOVERY_BUDGET_MS, signal }
        )
      );
      // Abandoned because this component unmounted mid-recovery — the user
      // left Explore and took the modal with them. Reject with an AbortError,
      // which the modal reads as "nothing to show" rather than a generation
      // failure worth notifying about.
      if (result.aborted) {
        throw new DOMException('Create idea content generation was abandoned', 'AbortError');
      }
      // Still swallows HTTP/network failures and resolves with
      // { success: false } — now only after trying to recover the result from
      // the chat-request row. Throw so the modal surfaces an error
      // notification instead of silently rendering blank title/description.
      if (result.success === false) {
        throw new Error(result.message || 'Failed to generate idea content');
      }
      const artifact = result.artifacts?.[0];
      if (artifact?.title || artifact?.description) {
        return { title: artifact.title ?? '', description: artifact.description ?? '' };
      } else if (!result.artifacts?.length || !artifact?.title || !artifact?.description) {
        showNotification({
          title: 'Could not generate title and description',
          message: 'Fill in the title and description manually.',
          color: 'red',
          autoClose: 20000
        });
        // Close the popup and reveal the hidden query + agent response in chat
        setCreateIdeaModalOpen(false);
        setPendingRevealHiddenPair(true);
        refetchMessages();
        return { title: '', description: '' };
      }
      return { title: '', description: '' };
    },
  });

  const prevBackendProcessingRef = useRef(false);
  useEffect(() => {
    if (prevBackendProcessingRef.current && !isBackendProcessing) {
      refetchMessages();
    }
    prevBackendProcessingRef.current = !!isBackendProcessing;
  }, [isBackendProcessing, refetchMessages]);

  const lastMessageIsAgent = messages.length > 1 && messages[messages.length - 1]?.messageType === 'agent';

  const isSendingForThisSession =
    !lastMessageIsAgent &&
    (isBackendProcessing ||
      (isMessageSending &&
        messageSendingSessionId !== null &&
        (messageSendingSessionId === effectiveSessionId ||
          (messageSendingSessionId === '' && !effectiveSessionId && Boolean(optimisticUserMessage)))));

  useEffect(() => {
    if (effectiveSessionId && isNewSession) {
      useDiscoveryStore.getState().setIsNewSession(false);
      useDiscoveryStore.getState().setOptimisticUserMessage(null);
    }
  }, [effectiveSessionId, isNewSession]);

  // --- Deep-link loader: ?artifactId=<ObjectId> ------------------------------
  // Resolves the artifact, auto-switches into its owning session if needed,
  // paginates until the message is loaded, then scrolls to the card and
  // opens the evidence panel. Guarded by lastResolvedDeepLinkArtifactId so it
  // runs at most once per unique id.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlArtifactId = searchParams.get('artifactId');
  const urlIdeaId = useExploreIdeaIdUrlParam(effectiveSessionId);
  const [pendingDeepLinkMessageId, setPendingDeepLinkMessageId] = useState<string | null>(null);

  useEffect(() => {
    if (!urlIdeaId) return;
    const store = useDiscoveryStore.getState();
    store.setInsightsPanelOpen(true);
    handleLeftPanelCollapse?.();
  }, [urlIdeaId, handleLeftPanelCollapse]);
  useEffect(() => {
    if (urlArtifactId) return;
    const store = useDiscoveryStore.getState();
    if (store.lastResolvedDeepLinkArtifactId !== null) {
      store.setLastResolvedDeepLinkArtifactId(null);
    }
  }, [urlArtifactId]);

  useEffect(() => {
    if (!urlArtifactId) return;

    const lastResolved = useDiscoveryStore.getState().lastResolvedDeepLinkArtifactId;
    if (lastResolved === urlArtifactId) return;

    let cancelled = false;

    (async () => {
      const resolved = await fetchArtifactById(urlArtifactId, auth0);
      if (cancelled) return;

      if (!resolved) {
        showNotification({
          message: 'This artifact is no longer available.',
          color: 'red',
        });
        setSearchParams((prev) => {
          prev.delete('artifactId');
          prev.delete('ideaId');
          prev.delete('ideasPage');
          return prev;
        }, { replace: true });
        useDiscoveryStore.getState().setLastResolvedDeepLinkArtifactId(urlArtifactId);
        return;
      }

      useDiscoveryStore.getState().setLastResolvedDeepLinkArtifactId(urlArtifactId);
      const recordId = resolved.artifact._id ?? urlArtifactId;
      useDiscoveryStore.getState().setActiveArtifactId(recordId);
      useDiscoveryStore.getState().setActiveArtifactRecordId(recordId);

      // The URL sessionId param matches what ExploreSessionList writes:
      // prefer the Mongo `_id` string (`session.id`), fall back to the
      // stable `sessionId` when the backend didn't return an `_id`.
      // Both are accepted by `getDiscoverySessionByIdOrSessionId` on the
      // backend, so deep-links for existing sessions in either form
      // resolve correctly.
      const targetSessionUrlId = resolved.session.id ?? resolved.session.sessionId;
      const isAlreadyActive =
        effectiveSessionId === targetSessionUrlId ||
        effectiveSessionId === resolved.session.sessionId ||
        effectiveSessionId === resolved.session.id;

      setPendingDeepLinkMessageId(resolved.messageId);
      if (!isAlreadyActive) {
        // Auto-switching sessions to resolve a deep-link — must preserve
        // `artifactId` in the URL so the in-session effect below can
        // pick up the pending deep-link once messages for the new
        // session finish loading.
        setSessionId(targetSessionUrlId, { preserveArtifactId: true });
      }
    })();

    return () => { cancelled = true; };
  }, [urlArtifactId, auth0, effectiveSessionId, setSessionId, setSearchParams]);

  // Once the session is loaded and we have a pending deep-link messageId,
  // paginate back until the message is available, then scroll + open panel.
  useEffect(() => {
    if (!urlArtifactId || !pendingDeepLinkMessageId) return;
    if (!effectiveSessionId) return;
    if (isLoading || isFetchingNextPage) return;

    const targetMessage = messages.find(
      (m) => (m._id && m._id === pendingDeepLinkMessageId) || m.id === pendingDeepLinkMessageId
    );
    if (!targetMessage) {
      if (hasNextPage) {
        fetchNextPage();
      } else {
        showNotification({
          message: 'This artifact is no longer available.',
          color: 'red',
        });
        setPendingDeepLinkMessageId(null);
        setSearchParams((prev) => {
          prev.delete('artifactId');
          prev.delete('ideaId');
          prev.delete('ideasPage');
          return prev;
        }, { replace: true });
      }
      return;
    }

    const artifact = targetMessage.metadata?.artifacts?.find(
      (a) => a._id === urlArtifactId
    );
    if (!artifact) {
      setPendingDeepLinkMessageId(null);
      return;
    }

    const store = useDiscoveryStore.getState();
    store.setActiveArtifactRecordId(artifact._id ?? urlArtifactId);
    store.setActiveMessageTimestamp(new Date(targetMessage.timestamp));

    if (artifact.type === 'ideas_list') {
      store.setArtifactEvidenceIds([]);
      store.setActiveEvidenceIds([]);
      store.setActiveEvidenceLinkText('');
      store.setActiveInsightSummary('');
      store.setActiveArtifactId(artifact._id ?? urlArtifactId);
      store.setIdeasListUiForArtifact(urlArtifactId, {
        page: parseIdeasPageParam(searchParams.get('ideasPage')),
        selectedIdeaId: searchParams.get('ideaId')?.trim() || undefined,
      });
    } else {
      store.setInsightsPanelOpen(true);
      store.setArtifactEvidenceIds(artifact.evidence_ids);
      store.setActiveEvidenceIds(artifact.evidence_ids);
      store.setActiveEvidenceLinkText(artifact.title);
      store.setActiveInsightSummary(artifact.description ?? '');
      store.setActiveArtifactId(artifact._id ?? urlArtifactId);
    }

    setPendingDeepLinkMessageId(null);

    requestAnimationFrame(() => {
      const selector = `[data-artifact-id="${CSS.escape(urlArtifactId)}"]`;
      const el = document.querySelector(selector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [
    urlArtifactId,
    pendingDeepLinkMessageId,
    effectiveSessionId,
    messages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    setSearchParams,
    searchParams,
  ]);

  const showActiveChat = !!effectiveSessionId || (isNewSession && !!optimisticUserMessage);
  const chatMaxWidth = showActiveChat ? 750 : 650;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [getStartedPage, setGetStartedPage] = useState(0);
  const [getStartedSlideDir, setGetStartedSlideDir] = useState<'next' | 'prev'>('next');
  const [getStartedDidNavigate, setGetStartedDidNavigate] = useState(false);

  const categories = useMemo(() => _.filter(prompts, 'isGroup'), [prompts]);

  const getStartedPageCount = Math.max(1, Math.ceil(categories.length / GET_STARTED_CARDS_PER_VIEW));
  const getStartedVisibleCategories = useMemo(
    () =>
      categories.slice(
        getStartedPage * GET_STARTED_CARDS_PER_VIEW,
        getStartedPage * GET_STARTED_CARDS_PER_VIEW + GET_STARTED_CARDS_PER_VIEW
      ),
    [categories, getStartedPage]
  );
  const canGetStartedPrev = getStartedPage > 0;
  const canGetStartedNext = getStartedPage < getStartedPageCount - 1;

  const scrollToPromptArea = () => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    document.getElementById('explore-discovery-composer')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  useEffect(() => {
    setGetStartedPage(0);
    setGetStartedDidNavigate(false);
  }, [categories.length]);

  const displayMessages = useMemo((): DiscoverySessionMessage[] => {
    // Active session — React Query's onMutate already injects the optimistic user
    // message into the cache, so `messages` already contains it. No need to
    // duplicate that logic with a Zustand-sourced optimisticUserMessage here.
    if (effectiveSessionId) return [...messages];
    // New chat — no session ID yet. Show the optimistic message from Zustand until
    // the session is created and the cache is seeded.
    if (optimisticUserMessage) {
      return [
        {
          id: 'optimistic',
          sessionId: '',
          userId: '',
          messageType: 'user',
          content: optimisticUserMessage,
          timestamp: new Date().toISOString(),
        },
      ];
    }
    return [];
  }, [effectiveSessionId, messages, optimisticUserMessage]);

  useEffect(() => {
    if (showActiveChat && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [showActiveChat, messages.length, optimisticUserMessage]);

  // ── Share session ──────────────────────────────────────────────────────────
  const [sharePopoverOpen, setSharePopoverOpen] = useState(false);
  const handleShareConfirm = useCallback(async () => {
    if (!sessionId) return;
    try {
      await shareDiscoverySession(sessionId, { getAccessTokenSilently });
      const sessionUrl = `${window.location.origin}${window.location.pathname}?sessionId=${encodeURIComponent(sessionId)}`;
      await navigator.clipboard.writeText(sessionUrl);
      showNotification({ message: 'Link copied to clipboard', color: 'green' });
      setSharePopoverOpen(false);
    } catch (err) {
      showNotification({
        message: err instanceof Error ? err.message : 'Failed to share session',
        color: 'red',
      });
    }
  }, [sessionId, getAccessTokenSilently]);

  // ── Create Idea (explore) ─────────────────────────────────────────────────
  const [createIdeaModalOpen, setCreateIdeaModalOpen] = useState(false);
  const [createIdeaSelectedOption, setCreateIdeaSelectedOption] = useState<CreateIdeaOption | null>(null);
  const [createIdeaArtifactId, setCreateIdeaArtifactId] = useState<string | null>(null);
  const [createIdeaActive, setCreateIdeaActive] = useState(false);
  /** Set to true to trigger a DB hidden=false update for the last CREATE_IDEA pair after messages refresh. */
  const [pendingRevealHiddenPair, setPendingRevealHiddenPair] = useState(false);
  /** Guards against firing duplicate PATCH requests while one is already in flight. */
  const revealHiddenPairInFlightRef = useRef(false);

  // When a create-idea call returns no artifact, unhide the message pair in the DB so the
  // user can see their query and the agent response that caused the failure.
  useEffect(() => {
    if (!pendingRevealHiddenPair || !messages.length || !effectiveSessionId) return;
    if (revealHiddenPairInFlightRef.current) return;
    const sorted = [...messages].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    const hiddenUserMsg = [...sorted].reverse().find(
      (m) => m.messageType === 'user' && m.hidden === true
    );
    if (!hiddenUserMsg) return;
    const msgId = hiddenUserMsg._id ?? hiddenUserMsg.id;
    if (!msgId) return;
    // Keep pendingRevealHiddenPair true until the PATCH succeeds so a failed request
    // can retry; use a ref to avoid firing duplicate requests while one is in flight.
    revealHiddenPairInFlightRef.current = true;
    // Persist hidden=false to DB; on success refetch so the chat thread updates.
    updateMessageHidden(effectiveSessionId, msgId, false, { getAccessTokenSilently })
      .then(() => {
        setPendingRevealHiddenPair(false);
        return refetchMessages();
      })
      .catch((err) => console.error('[ExploreMainContent] updateMessageHidden failed', err))
      .finally(() => {
        revealHiddenPairInFlightRef.current = false;
      });
  }, [pendingRevealHiddenPair, messages, effectiveSessionId, getAccessTokenSilently]);

  const pendingArtifactCreateIdeaTitle = useDiscoveryStore((s) => s.pendingArtifactCreateIdeaTitle);
  const pendingArtifactCreateIdeaArtifactId = useDiscoveryStore((s) => s.pendingArtifactCreateIdeaArtifactId);
  const setPendingArtifactCreateIdeaTitle = useDiscoveryStore((s) => s.setPendingArtifactCreateIdeaTitle);
  const setPendingArtifactCreateIdeaArtifactId = useDiscoveryStore((s) => s.setPendingArtifactCreateIdeaArtifactId);
  useEffect(() => {
    if (!pendingArtifactCreateIdeaTitle) return;
    setCreateIdeaSelectedOption({
      label: pendingArtifactCreateIdeaTitle,
      payload: pendingArtifactCreateIdeaTitle,
    });
    setCreateIdeaArtifactId(pendingArtifactCreateIdeaArtifactId);
    setCreateIdeaModalOpen(true);
    setPendingArtifactCreateIdeaTitle(null);
    setPendingArtifactCreateIdeaArtifactId(null);
  }, [pendingArtifactCreateIdeaTitle, pendingArtifactCreateIdeaArtifactId, setPendingArtifactCreateIdeaTitle, setPendingArtifactCreateIdeaArtifactId]);
  // Stores prefilled content when a suggested_idea artifact triggers auto-open
  const [suggestedIdeaContent, setSuggestedIdeaContent] = useState<{ title: string; description: string } | null>(null);
  // Ref mirror so fetchExploreCreateIdeaContent always reads the latest value even if
  // it was set in the same React flush that triggered the modal open.
  const suggestedIdeaContentRef = React.useRef<{ title: string; description: string } | null>(null);

  /** Called by ExploreChat when the API returns a suggested_idea artifact — no Zustand round-trip */
  const handleSuggestedIdeaReceived = React.useCallback(
    (artifact: { title: string; description: string }) => {
      const prefill = { title: artifact.title, description: artifact.description };
      suggestedIdeaContentRef.current = prefill;
      setSuggestedIdeaContent(prefill);
      setCreateIdeaSelectedOption(null);
      setCreateIdeaArtifactId(null);
      setCreateIdeaModalOpen(true);
    },
    []
  );

  /** Real user queries in this session (hidden CREATE_IDEA messages excluded). */
  const exploreUserQuestionsCount = useMemo(
    () =>
      messages.filter(
        (m) => m.messageType === 'user' && !isHiddenCreateIdeaUserMessage(m.content)
      ).length,
    [messages]
  );

  const exploreCreateIdeaOptions = useMemo(
    () => buildCreateIdeaOptions(messages),
    [messages]
  );

  /** Button click — enters inline options-list mode inside the composer. */
  const handleExploreCreateIdeaActivate = () => {
    setCreateIdeaActive(true);
  };

  const handleExploreCreateIdeaDismiss = () => {
    setCreateIdeaActive(false);
  };

  /** User picked a specific option from the list → open the modal. */
  const handleExploreCreateIdeaConfirm = (option: CreateIdeaOption) => {
    setCreateIdeaSelectedOption(option);
    setCreateIdeaArtifactId(null);
    setCreateIdeaActive(false);
    setCreateIdeaModalOpen(true);
  };

  // ─────────────────────────────────────────────────────────────────────────

  const resolvePromptCategory = (item: { categoryText: string; category?: string }): string | null => {
    if (item.category && typeof item.category === 'string') return item.category;
    return null;
  };

  const categoryIconMap: Record<string, typeof IconCategory> = {
    'churn': IconTrendingDown,
    'growth': IconGrowth,
    'competitive': IconSwords,
    'engagement': IconHeartHandshake,
    'general': IconCategory,
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: '#f8f8fb',
      }}
    >
      {/* Top bar: sidebar icon (top-left when collapsed) + tabs */}
      <Box
        sx={{
          flexShrink: 0,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '10px 32px',
          minHeight: 48,
        }}
      >
        {onExpandLeftSidebar && (
          <Transition mounted={Boolean(isLeftSidebarCollapsed)} transition="fade" duration={200}>
            {(styles) => (
              <ActionIcon
                size="lg"
                variant="subtle"
                onClick={onExpandLeftSidebar}
                aria-label="Open left sidebar"
                style={{ ...styles, position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}
                sx={{
                  color: '#495057',
                }}
              >
                <IconLayoutSidebarLeftExpand size={28} stroke={1} color={'#495057'}/>
              </ActionIcon>
            )}
          </Transition>
        )}
        {/* Centered column matching the chat content's width, so anything
            right-aligned here (e.g. the Share button) lines up with the
            chat window's right edge below rather than the full page edge. */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: chatMaxWidth,
              display: 'flex',
              alignItems: 'center',
              justifyContent: sessionId ? 'flex-end' : 'center',
              gap: 12,
              minWidth: 0,
            }}
          >
            {!sessionId && showExploreTopSegmentControl && (
              <>
                <ExploreButton tabType={'discovery'}/>
                <ExploreButton tabType={'ideasSpace'} disabled/>
                <ExploreButton tabType={'customer360'} disabled/>
              </>
            )}
            {sessionId && (
              <Popover
                opened={sharePopoverOpen}
                onClose={() => setSharePopoverOpen(false)}
                position="bottom-end"
                withArrow
                shadow="md"
              >
                <Popover.Target>
                  <Button
                    variant="outline"
                    rightIcon={<IconShare size={18} color="#212529" />}
                    size="sm"
                    onClick={() => setSharePopoverOpen((o) => !o)}
                    sx={{
                      height: 32,
                      borderColor: '#D8D8DB',
                      color: '#212529',
                      fontSize: 12,
                      fontWeight: 500,
                      ':hover': { backgroundColor: '#5C5CEB1A' },
                    }}
                  >
                    Share
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Text size="sm" mb="xs">
                    Make this session shareable and copy link?
                  </Text>
                  <Button size="xs" onClick={handleShareConfirm} disabled={!sessionId}>
                    Copy link
                  </Button>
                </Popover.Dropdown>
              </Popover>
            )}
          </Box>
        </Box>
      </Box>

      {/* Single scrollable area (scrollbar on the right of the page, ChatGPT-style) */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0 32px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: chatMaxWidth,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 0,
            marginTop: showActiveChat ? 16 : 80,
            paddingBottom: showActiveChat ? 50 : 24,
          }}
        >
          {showActiveChat ? (
            <ExploreChatMessageList
              messages={displayMessages}
              sessionId={effectiveSessionId}
              isLoading={isLoading}
              isSending={isSendingForThisSession}
              chatStatusError={session?.chatStatus === 'error' ? (session.chatStatusError ?? 'Unknown error') : null}
              handleLeftPanelCollapse={handleLeftPanelCollapse}
              artifactIdeaIdByAlias={artifactIdeaIdByAlias}
              scrollToPromptArea={scrollToPromptArea}
              onIdeaCreated={refetchSessionIdeas}
            />
          ) : (
            <>
              <ExploreBagelSparkleIcon/>
              <Text
                sx={{
                  marginTop: 20,
                  color: '#212529',
                  fontWeight: 600,
                  fontSize: '31px',
                  lineHeight: '28px',
                }}
              >
                Hi {getGreetingName(user)}
              </Text>
              <Text fz={15} c="#585C68" mt={10} mb={24}>
                What impact will you create for your company today?
              </Text>

              <Box sx={{width: '100%', alignSelf: 'stretch'}}>
                <ExploreChat isNewChat onSuggestedIdeaReceived={handleSuggestedIdeaReceived} blockComposerSend={createIdeaContentMutation.isPending}/>
              </Box>

              <Text
                sx={{
                  color: '#585C68',
                  fontWeight: 500,
                  fontSize: 14,
                  marginTop: 16,
                  marginBottom: 12,
                  width: '100%',
                  alignSelf: 'stretch',
                }}
              >
                Get started
              </Text>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 8,
                  width: '100%',
                  minWidth: 0,
                  alignSelf: 'stretch',
                }}
              >
                <ActionIcon
                  type="button"
                  variant="subtle"
                  size="lg"
                  aria-label="Previous prompts"
                  disabled={!canGetStartedPrev}
                  onClick={() => {
                    setGetStartedDidNavigate(true);
                    setGetStartedSlideDir('prev');
                    setGetStartedPage((p) => Math.max(0, p - 1));
                  }}
                  sx={{
                    alignSelf: 'center',
                    flexShrink: 0,
                    color: '#495057',
                    '&:disabled': {opacity: 0.35},
                  }}
                >
                  <IconChevronLeft size={22} stroke={1.5}/>
                </ActionIcon>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    key={getStartedPage}
                    sx={{
                      display: 'grid',
                      width: '100%',
                      minWidth: 0,
                      gap: 12,
                      gridTemplateColumns:
                        getStartedVisibleCategories.length >= 2
                          ? 'minmax(0, 1fr) minmax(0, 1fr)'
                          : 'minmax(0, 1fr)',
                      '@keyframes exploreGetStartedEnterNext': {
                        from: {opacity: 0, transform: 'translateX(16px)'},
                        to: {opacity: 1, transform: 'translateX(0)'},
                      },
                      '@keyframes exploreGetStartedEnterPrev': {
                        from: {opacity: 0, transform: 'translateX(-16px)'},
                        to: {opacity: 1, transform: 'translateX(0)'},
                      },
                      ...(getStartedDidNavigate
                        ? {
                          animation:
                            getStartedSlideDir === 'next'
                              ? 'exploreGetStartedEnterNext 320ms cubic-bezier(0.33, 1, 0.68, 1) both'
                              : 'exploreGetStartedEnterPrev 320ms cubic-bezier(0.33, 1, 0.68, 1) both',
                        }
                        : {}),
                    }}
                  >
                    {getStartedVisibleCategories.map((item, index) => {
                      const {categoryText, category} = item;
                      const CurrentIcon = categoryIconMap[category] ?? IconCategory;
                      return (
                        <Box
                          key={getStartedPage * GET_STARTED_CARDS_PER_VIEW + index}
                          component="button"
                          type="button"
                          onClick={() => {
                            setInputValue('');
                            setDiscoveryPromptCategory(resolvePromptCategory(item), categoryText);
                          }}
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            padding: '12px 16px',
                            borderRadius: 16,
                            backgroundColor: 'rgba(92, 92, 235, 0.08)',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            '&:hover': {
                              backgroundColor: 'rgba(92, 92, 235, 0.12)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '& svg': {
                                width: '20px !important',
                                height: '20px !important',
                                minWidth: 20,
                                minHeight: 20,
                                maxWidth: 20,
                                maxHeight: 20,
                              },
                            }}
                          >
                            <CurrentIcon size={20} stroke={2} color="#212529"/>
                          </Box>
                          <Text size="sm" fw={500} c="#212529" sx={{lineHeight: 1.4}}>
                            {categoryText}
                          </Text>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <ActionIcon
                  type="button"
                  variant="subtle"
                  size="lg"
                  aria-label="Next prompts"
                  disabled={!canGetStartedNext}
                  onClick={() => {
                    setGetStartedDidNavigate(true);
                    setGetStartedSlideDir('next');
                    setGetStartedPage((p) => Math.min(getStartedPageCount - 1, p + 1));
                  }}
                  sx={{
                    alignSelf: 'center',
                    flexShrink: 0,
                    color: '#495057',
                    '&:disabled': {opacity: 0.35},
                  }}
                >
                  <IconChevronRight size={22} stroke={1.5}/>
                </ActionIcon>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Input fixed at bottom */}
      {showActiveChat && (
        <Box sx={{flexShrink: 0, padding: '0 32px 40px', display: 'flex', justifyContent: 'center'}}>
          <Box sx={{width: '100%', maxWidth: chatMaxWidth}}>
            {/* Create Idea trigger — visible only when not in inline-prompt mode */}
            {exploreUserQuestionsCount > 0 && !createIdeaActive && (
              <Flex mb={8} pl={2}>
                <CreateIdeaButton
                  options={exploreCreateIdeaOptions}
                  onActivate={handleExploreCreateIdeaActivate}
                  disabled={isSendingForThisSession || createIdeaModalOpen}
                />
              </Flex>
            )}
            {/* ExploreChat receives create-idea props so the inline prompt renders inside the composer */}
            <ExploreChat
              createIdeaActive={createIdeaActive}
              createIdeaOptions={exploreCreateIdeaOptions}
              onCreateIdeaConfirm={handleExploreCreateIdeaConfirm}
              onCreateIdeaDismiss={handleExploreCreateIdeaDismiss}
              blockComposerSend={createIdeaContentMutation.isPending}
              onSuggestedIdeaReceived={handleSuggestedIdeaReceived}
            />
          </Box>
        </Box>
      )}

      {/* Create Idea modal (explore) */}
      <CreateIdeaFromChatModal
        opened={createIdeaModalOpen}
        onClose={() => {
          setCreateIdeaModalOpen(false);
          setCreateIdeaArtifactId(null);
        }}
        selectedOption={createIdeaSelectedOption}
        fetchContent={createIdeaContentMutation.mutateAsync}
        ideaId=""
        createdBy="agent"
        sessionId={effectiveSessionId}
        messageId={createIdeaSelectedOption?.messageId}
        artifactId={createIdeaArtifactId ?? undefined}
        onCreated={() => {
          // Artifact creates don't produce an idea_created chat event (backend
          // only fires that when createdBy === 'agent'), so skip the message
          // refetch for the artifact path — it would be a no-op anyway.
          if (!createIdeaArtifactId) {
            // Delay messages refetch by 10 s — the backend needs time to persist
            // the idea-created event before the messages API returns it.
            setTimeout(() => {
              refetchMessages();
            }, 10000);
          }
          // The new idea links artifacts to it via the sessionIdeas query.
          // Without this, artifactIdeaIdByAlias stays stale (30s) and cards
          // keep missing the link to the just-created idea.
          refetchSessionIdeas();
        }}
      />
    </Box>
  );
}

export default ExploreMainContent;